import { useState, useEffect, useRef } from 'react';

/**
 * Tells whether the element attached to the returned `ref` is (about to be) in the viewport.
 *
 * - `once` (default) - the observer is disconnected after the first intersection and
 *   `inViewport` stays `true`, so already revealed content is never unmounted again.
 * - The initial value is always `false` (on the server and on the first client render), so the
 *   markup is the same for hydration.
 * - While `enabled` is `false` nothing is observed, so the element can not become visible.
 * - Where `IntersectionObserver` is not available, the element is treated as visible.
 *
 * @returns {[import('react').MutableRefObject<any>, boolean]} `[ref, inViewport]`
 */
export const useInViewport = ({
    rootMargin = '0px',
    threshold = 0,
    once = true,
    enabled = true,
} = {}) => {
    const ref = useRef(null);
    const [inViewport, setInViewport] = useState(false);

    useEffect(() => {
        if (!enabled || (once && inViewport)) {
            return;
        }

        const node = ref.current;

        if (!node) {
            return;
        }

        if (typeof IntersectionObserver === 'undefined') {
            setInViewport(true);
            return;
        }

        const observer = new IntersectionObserver(
            entries => {
                const isIntersecting = entries.some(({ isIntersecting }) => isIntersecting);

                if (once && isIntersecting) {
                    observer.disconnect();
                }

                setInViewport(isIntersecting);
            },
            { rootMargin, threshold }
        );

        observer.observe(node);

        return () => observer.disconnect();
    }, [rootMargin, threshold, once, enabled, inViewport]);

    return [ref, inViewport];
};
